import { Node, type Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection, Selection, type Transaction } from '@tiptap/pm/state';

const PENCIL_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>';

// Beeldgroep: een blok met 2 tot 12 gewone image-nodes in 2, 3, 4 of 6 kolommen, met één bijschrift.
// Serialiseert naar
//   <figure class="dibbi-beeldgroep dibbi-beeldgroep-N">
//     <div class="dibbi-beeldgroep-raster"><img …><img …></div>
//     <figcaption>…</figcaption>            (alleen met bijschrift)
//   </figure>
// zonder inline-stijlen: de stijlregels staan per weergave apart (zie beeldgroepCss.test.ts).

export const IMAGE_GROUP_COLUMNS = [2, 3, 4, 6] as const;
export type ImageGroupColumns = (typeof IMAGE_GROUP_COLUMNS)[number];
export const IMAGE_GROUP_MAX = 12;

export function isImageGroupColumns(n: number): n is ImageGroupColumns {
  return (IMAGE_GROUP_COLUMNS as readonly number[]).includes(n);
}

// Leest het kolomaantal uit de klasse dibbi-beeldgroep-N; onbekend of ontbrekend wordt 3.
export function columnsFromClass(className: string | null): ImageGroupColumns {
  const match = (className ?? '').match(/(?:^|\s)dibbi-beeldgroep-(\d+)(?:\s|$)/);
  const n = match ? Number(match[1]) : NaN;
  return isImageGroupColumns(n) ? n : 3;
}

// Standaard kolomaantal bij het aanmaken: 2 bij 2 of 4 foto's, anders 3.
export function defaultColumnsFor(count: number): ImageGroupColumns {
  return count === 2 || count === 4 ? 2 : 3;
}

const captionOf = (element: HTMLElement): string | null => {
  const figcaption = Array.from(element.children).find((child) => child.tagName === 'FIGCAPTION');
  return figcaption?.textContent || null;
};

export const BlogImageGroup = Node.create({
  name: 'imageGroup',
  group: 'block',
  content: 'image+',
  isolating: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      columns: {
        default: 3,
        parseHTML: (element: HTMLElement) => columnsFromClass(element.getAttribute('class')),
        renderHTML: () => ({}),
      },
      caption: {
        default: null,
        parseHTML: captionOf,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure.dibbi-beeldgroep',
        priority: 100,
        contentElement: '.dibbi-beeldgroep-raster',
        // Zonder raster-div weigeren; de HTML valt dan terug op de figure-regel van BlogImage.
        getAttrs: (element: HTMLElement) => (element.querySelector('.dibbi-beeldgroep-raster') ? null : false),
      },
    ];
  },

  // Bewust geen mergeAttributes met this.options.HTMLAttributes (zelfde keuze als bij BlogImage):
  // de klassen van de figure zijn het contract voor de CSS.
  renderHTML({ node }) {
    const figure = { class: `dibbi-beeldgroep dibbi-beeldgroep-${node.attrs.columns}` };
    const raster = ['div', { class: 'dibbi-beeldgroep-raster' }, 0] as const;
    return node.attrs.caption
      ? ['figure', figure, raster, ['figcaption', {}, node.attrs.caption]]
      : ['figure', figure, raster];
  },

  // Plain-DOM node view (zelfde stijl als BlogTeaserBox): de figure is het blok, het raster is de
  // contentDOM waarin ProseMirror de beelden beheert; potlood en bijschrift beheren we zelf.
  addNodeView() {
    return ({ node, getPos }) => {
      const dom = document.createElement('figure');
      const badge = document.createElement('span');
      badge.className = 'dibbi-beeldgroep-bewerk';
      badge.setAttribute('aria-hidden', 'true');
      badge.contentEditable = 'false';
      badge.innerHTML = PENCIL_SVG;
      const raster = document.createElement('div');
      raster.className = 'dibbi-beeldgroep-raster';
      const caption = document.createElement('figcaption');
      caption.contentEditable = 'false';

      const apply = (current: PMNode) => {
        // dom.className overschrijven zou klassen wissen die ProseMirror zelf op deze dom zet,
        // zoals ProseMirror-selectednode bij een node-selectie op de groep; daarom toggelen we
        // losse klassen in plaats van het hele attribuut te vervangen.
        dom.classList.add('dibbi-beeldgroep');
        for (const n of IMAGE_GROUP_COLUMNS) dom.classList.toggle(`dibbi-beeldgroep-${n}`, n === current.attrs.columns);
        caption.textContent = current.attrs.caption || '';
        caption.hidden = !current.attrs.caption;
      };
      apply(node);
      dom.append(badge, raster, caption);

      // Klik buiten de foto's (rand, potlood, bijschrift) opent het bewerkvenster in BlogEdit;
      // een klik op een foto loopt via handleClickOn daar. ProseMirror's handleClickOn in BlogEdit
      // vangt de meeste klikken al op; dit event is het vangnet voor klikken buiten de contentDOM
      // (bijschrift, rand), dubbel openen is onschadelijk omdat BlogEdit dezelfde positie zet.
      const onClick = (event: MouseEvent) => {
        if (raster.contains(event.target as globalThis.Node)) return;
        event.preventDefault();
        const pos = getPos();
        if (typeof pos !== 'number') return;
        document.dispatchEvent(new CustomEvent('imageGroupEdit', { detail: { pos } }));
      };
      dom.addEventListener('click', onClick);

      return {
        dom,
        contentDOM: raster,
        update: (updated: PMNode) => {
          if (updated.type.name !== 'imageGroup') return false;
          apply(updated);
          return true;
        },
        // Alleen wijzigingen in het raster zijn inhoud; de rest beheren we zelf.
        ignoreMutation: (mutation) => mutation.type !== 'selection' && !raster.contains(mutation.target),
        destroy: () => dom.removeEventListener('click', onClick),
      };
    };
  },
});

export type ImageGroupHit = { pos: number; node: PMNode };
export type ImageGroupImage = { src: string; uploading?: boolean; alt?: string | null };
export type RemoveFromGroupResult = 'group' | 'unwrapped' | 'removed';

const groupAt = (editor: Editor, pos: number): PMNode | null => {
  if (editor.isDestroyed || pos < 0 || pos > editor.state.doc.content.size) return null;
  const node = editor.state.doc.nodeAt(pos);
  return node && node.type.name === 'imageGroup' ? node : null;
};

// Positie van kind `index` binnen de groep die op groupPos begint.
const childPosition = (groupPos: number, group: PMNode, index: number): number => {
  let pos = groupPos + 1;
  for (let i = 0; i < index; i++) pos += group.child(i).nodeSize;
  return pos;
};

const childrenOf = (group: PMNode): PMNode[] => {
  const children: PMNode[] = [];
  group.forEach((child) => children.push(child));
  return children;
};

const imageAttrs = (image: ImageGroupImage) => ({
  src: image.src,
  uploading: Boolean(image.uploading),
  alt: image.alt ?? null,
});

// Geeft de groep op `pos` zelf, of de groep waar `pos` in valt (bijvoorbeeld de positie van een beeld erin).
export function findImageGroupAt(editor: Editor, pos: number): ImageGroupHit | null {
  if (editor.isDestroyed) return null;
  const { doc } = editor.state;
  if (pos < 0 || pos > doc.content.size) return null;
  const direct = doc.nodeAt(pos);
  if (direct && direct.type.name === 'imageGroup') return { pos, node: direct };
  const $pos = doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'imageGroup') return { pos: $pos.before(depth), node };
  }
  return null;
}

// Momentopname van een groep voor het bewerkvenster: alles wat dat venster laat zien, zonder dat het
// zelf in de ProseMirror-boom hoeft te graven.
export type ImageGroupSnapshot = {
  pos: number;
  columns: ImageGroupColumns;
  caption: string | null;
  images: { src: string; alt: string | null; uploading: boolean }[];
};

// Leest de groep die exact op `pos` begint. Staat daar geen groep (of alleen een beeld ín een groep),
// dan null: het venster hoort dan te sluiten, niet stilletjes op een andere groep over te springen.
export function readImageGroup(editor: Editor, pos: number): ImageGroupSnapshot | null {
  const hit = findImageGroupAt(editor, pos);
  if (!hit || hit.pos !== pos) return null;
  const images: ImageGroupSnapshot['images'] = [];
  hit.node.forEach((child) => {
    images.push({
      src: child.attrs.src ?? '',
      alt: child.attrs.alt ?? null,
      uploading: Boolean(child.attrs.uploading),
    });
  });
  return { pos, columns: hit.node.attrs.columns, caption: hit.node.attrs.caption ?? null, images };
}

// Volgt de groep door één transactie heen: posities schuiven als er eerder in het document iets
// verandert (bijvoorbeeld een mislukte upload die verdwijnt). Null betekent: op de nieuwe plek staat
// geen groep meer. Wie daarop reageert met een handeling die zélf een transactie veroorzaakt, moet
// zich eerst afmelden als luisteraar; zie de test bij deze functie.
export function followImageGroup(editor: Editor, transaction: Transaction, pos: number): ImageGroupSnapshot | null {
  return readImageGroup(editor, transaction.mapping.map(pos));
}

// Voegt een groep in als blok op de cursor (of op `pos`). Focus zet de aanroeper zelf.
export function insertImageGroup(
  editor: Editor,
  images: ImageGroupImage[],
  columns: ImageGroupColumns,
  pos?: number
): boolean {
  if (editor.isDestroyed || images.length === 0) return false;
  const content = {
    type: 'imageGroup',
    attrs: { columns, caption: null },
    content: images.map((image) => ({ type: 'image', attrs: imageAttrs(image) })),
  };
  if (typeof pos === 'number') return editor.commands.insertContentAt(pos, content);
  // insertContent vervangt de huidige selectie. Staat de cursor als node-selectie op een beeld
  // ín een bestaande groep of op de groep zelf, dan zou dat de groep splitsen of overschrijven.
  // Val in dat geval terug op invoegen ná die groep, met insertContentAt. Alleen bij een echte
  // NodeSelection: een gap cursor vlak vóór een groep heeft toevallig dezelfde `from` als de
  // groep zelf (from === groupPos), en zou dan ook via deze val ná die groep belanden in plaats
  // van ervoor.
  const { selection } = editor.state;
  if (selection instanceof NodeSelection) {
    const hit = findImageGroupAt(editor, selection.from);
    if (hit) return editor.commands.insertContentAt(hit.pos + hit.node.nodeSize, content);
    // Een geselecteerd los beeld (of ander blok) mag ook niet vervangen worden: erachter invoegen.
    return editor.commands.insertContentAt(selection.to, content);
  }
  return editor.commands.insertContent(content);
}

// Zet de cursor als tekstselectie ná het beeld met deze src, of ná de groep waar dat beeld in zit.
// Na insertContent blijft het ingevoegde beeld anders als node-selectie staan en typt de gebruiker het weg.
export function moveCursorAfterImage(editor: Editor, src: string): boolean {
  if (editor.isDestroyed) return false;
  const { doc } = editor.state;
  // Via een lijst in plaats van een losse variabele: het eerste beeld met deze src is genoeg.
  const hits: { pos: number; nodeSize: number }[] = [];
  doc.descendants((node, pos) => {
    if (hits.length > 0) return false;
    if (node.type.name !== 'image' || node.attrs.src !== src) return true;
    const $pos = doc.resolve(pos);
    // Een beeld ín een groep: spring over de hele groep heen, niet tussen de beelden in.
    if ($pos.parent.type.name === 'imageGroup') hits.push({ pos: $pos.before(), nodeSize: $pos.parent.nodeSize });
    else hits.push({ pos, nodeSize: node.nodeSize });
    return false;
  });
  if (hits.length === 0) return false;
  const after = hits[0].pos + hits[0].nodeSize;
  const { tr } = editor.state;
  // Bewust de eerstvolgende tekstplek zoeken en niet Selection.near: die valt terug op een
  // node-selectie (op het beeld zelf, of op een volgend beeld) en dan wist de eerste toetsaanslag
  // alsnog een foto. Staat het beeld helemaal achteraan, dan is er niets om in te typen; zet er
  // dan een lege alinea neer. In de editor doet TrailingNode dat meestal al.
  if (!Selection.findFrom(tr.doc.resolve(after), 1, true)) {
    const paragraph = editor.schema.nodes.paragraph;
    if (!paragraph) return false;
    tr.insert(after, paragraph.create());
  }
  const target = Selection.findFrom(tr.doc.resolve(after), 1, true);
  if (!target) return false;
  tr.setSelection(target);
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}

export function setImageGroupAttrs(
  editor: Editor,
  groupPos: number,
  attrs: { columns?: ImageGroupColumns; caption?: string | null }
): boolean {
  const group = groupAt(editor, groupPos);
  if (!group) return false;
  // Alleen expliciet meegegeven sleutels overschrijven; anders reset een aanroeper die per ongeluk
  // `columns: undefined` meestuurt de kolommen naar de standaardwaarde.
  const defined = Object.fromEntries(Object.entries(attrs).filter(([, value]) => value !== undefined));
  editor.view.dispatch(editor.state.tr.setNodeMarkup(groupPos, undefined, { ...group.attrs, ...defined }));
  return true;
}

export function setGroupImageAlt(editor: Editor, groupPos: number, index: number, alt: string | null): boolean {
  const group = groupAt(editor, groupPos);
  if (!group || index < 0 || index >= group.childCount) return false;
  const child = group.child(index);
  const pos = childPosition(groupPos, group, index);
  editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...child.attrs, alt: alt || null }));
  return true;
}

export function moveImageInGroup(editor: Editor, groupPos: number, from: number, to: number): boolean {
  const group = groupAt(editor, groupPos);
  if (!group) return false;
  const count = group.childCount;
  if (from === to || from < 0 || to < 0 || from >= count || to >= count) return false;
  const children = childrenOf(group);
  const [moved] = children.splice(from, 1);
  children.splice(to, 0, moved);
  editor.view.dispatch(editor.state.tr.replaceWith(groupPos + 1, groupPos + 1 + group.content.size, children));
  return true;
}

// Blijft er één beeld over, dan wordt de groep dat losse beeld; blijft er niets over, dan verdwijnt de groep.
export function removeImageFromGroup(editor: Editor, groupPos: number, index: number): RemoveFromGroupResult | false {
  const group = groupAt(editor, groupPos);
  if (!group || index < 0 || index >= group.childCount) return false;
  const remaining = childrenOf(group).filter((_, i) => i !== index);
  const { tr } = editor.state;
  let result: RemoveFromGroupResult;
  if (remaining.length === 0) {
    tr.delete(groupPos, groupPos + group.nodeSize);
    result = 'removed';
  } else if (remaining.length === 1) {
    tr.replaceWith(groupPos, groupPos + group.nodeSize, remaining[0]);
    result = 'unwrapped';
  } else {
    const pos = childPosition(groupPos, group, index);
    tr.delete(pos, pos + group.child(index).nodeSize);
    result = 'group';
  }
  editor.view.dispatch(tr);
  return result;
}

export function appendImagesToGroup(editor: Editor, groupPos: number, images: ImageGroupImage[]): boolean {
  const group = groupAt(editor, groupPos);
  if (!group || images.length === 0) return false;
  const imageType = editor.schema.nodes.image;
  const nodes = images.map((image) => imageType.create(imageAttrs(image)));
  editor.view.dispatch(editor.state.tr.insert(groupPos + 1 + group.content.size, nodes));
  return true;
}

// Veiligheidsnet vóór opslaan, naast removeBlobImages: een groep met één beeld wordt een los beeld,
// beelden zonder src vallen weg, een groep zonder bruikbaar beeld verdwijnt. Buiten de undo-geschiedenis,
// want de gebruiker maakte deze wijziging niet zelf.
export function normalizeImageGroups(editor: Editor): number {
  if (editor.isDestroyed) return 0;
  const groups: ImageGroupHit[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'imageGroup') {
      groups.push({ pos, node });
      return false;
    }
    return true;
  });

  const { tr } = editor.state;
  let changed = 0;
  // Van achteren naar voren, dan schuiven eerdere posities niet.
  for (let i = groups.length - 1; i >= 0; i--) {
    const { pos, node } = groups[i];
    const valid = childrenOf(node).filter((child) => typeof child.attrs.src === 'string' && child.attrs.src !== '');
    if (valid.length === node.childCount && valid.length >= 2) continue;
    if (valid.length === 0) tr.delete(pos, pos + node.nodeSize);
    else if (valid.length === 1) tr.replaceWith(pos, pos + node.nodeSize, valid[0]);
    else tr.replaceWith(pos + 1, pos + 1 + node.content.size, valid);
    changed += 1;
  }
  if (changed === 0) return 0;
  tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return changed;
}
